import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, EnvelopeSimple, WhatsappLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SectionHeader from "@/components/site/SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA = "https://wa.me/919057862163?text=" + encodeURIComponent("Hello CRETRE Team, I would like to discuss my manpower/recruitment requirement.");

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", requirement: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact-enquiries`, form);
      toast.success("Message sent", { description: "Our team will get back to you shortly." });
      setForm({ name: "", company: "", phone: "", email: "", requirement: "", message: "" });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="section-pad bg-white">
        <div className="container-tight">
          <SectionHeader overline="Contact" title="Let’s Talk" subtitle="For staffing, recruitment, payroll or workforce management — talk to our team." />

          <div className="mt-14 grid lg:grid-cols-[1fr,1.3fr] gap-10">
            <div className="space-y-4">
              <div className="bg-navy-900 text-white rounded-3xl p-8">
                <p className="overline text-gold-400">CRETRE</p>
                <p className="mt-3 font-serif text-2xl">Ahmedabad, Gujarat, India</p>
                <ul className="mt-6 space-y-4 text-sm">
                  <li className="flex items-start gap-3"><MapPin size={20} className="text-gold-400 mt-0.5" /><span>Ahmedabad, Gujarat, India</span></li>
                  <li className="flex items-center gap-3"><Phone size={20} className="text-gold-400" /><a href="tel:+919057862163" className="hover:text-gold-400">+91 90578 62163</a></li>
                  <li className="flex items-center gap-3"><EnvelopeSimple size={20} className="text-gold-400" /><a href="mailto:business@infocretregroup.in" className="hover:text-gold-400 break-all">business@infocretregroup.in</a></li>
                </ul>
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#25D366] hover:bg-[#1FBE59] text-white font-medium btn-lift">
                  <WhatsappLogo size={20} weight="fill" /> Chat on WhatsApp
                </a>
              </div>
              <div data-testid="contact-map" className="rounded-3xl overflow-hidden border border-border bg-secondary aspect-[4/3]">
                <iframe
                  title="CRETRE Ahmedabad Map"
                  src="https://www.google.com/maps?q=Ahmedabad,+Gujarat,+India&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>

            <form onSubmit={submit} className="bg-secondary/40 rounded-3xl border border-border p-6 lg:p-10 grid gap-5 sm:grid-cols-2">
              <F label="Name*"><Input required value={form.name} onChange={on("name")} data-testid="contact-name" /></F>
              <F label="Company"><Input value={form.company} onChange={on("company")} data-testid="contact-company" /></F>
              <F label="Phone*"><Input required value={form.phone} onChange={on("phone")} data-testid="contact-phone" /></F>
              <F label="Email*"><Input required type="email" value={form.email} onChange={on("email")} data-testid="contact-email" /></F>
              <div className="sm:col-span-2">
                <F label="Requirement"><Input value={form.requirement} onChange={on("requirement")} data-testid="contact-requirement" placeholder="Staffing / Recruitment / Payroll / Compliance..." /></F>
              </div>
              <div className="sm:col-span-2">
                <F label="Message*"><Textarea required rows={5} value={form.message} onChange={on("message")} data-testid="contact-message" /></F>
              </div>
              <div className="sm:col-span-2 flex items-center justify-end">
                <Button data-testid="contact-submit-btn" type="submit" disabled={submitting} className="bg-navy-800 hover:bg-navy-700 text-white rounded-full h-12 px-8 btn-lift">
                  {submitting ? "Sending..." : "Send Enquiry"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-navy-800 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
